import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { useState, useCallback, useEffect, useLayoutEffect, useRef, lazy, Suspense, type ReactNode } from "react";
import { usePathname } from "@/hooks/usePathname";

// Monkey-patch pushState/replaceState once so we get events on every nav
// (browsers only fire popstate for back/forward, not for SPA navigations).
if (typeof window !== "undefined" && !(window as any).__historyPatched) {
  (window as any).__historyPatched = true;
  const _push = window.history.pushState;
  const _replace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const r = _push.apply(this, args as any);
    window.dispatchEvent(new Event("pushstate"));
    return r;
  };
  window.history.replaceState = function (...args) {
    const r = _replace.apply(this, args as any);
    window.dispatchEvent(new Event("replacestate"));
    return r;
  };
}

/**
 * Reset scroll to top on cross-route navigation. Skips when the URL has a
 * hash — those are handled by per-page smooth-scroll logic (e.g. /#pricing).
 * useLayoutEffect runs before paint so the user never sees the old position.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/**
 * Loads `foreman-ui-app.css` + `foreman-ui-automations.css` on demand
 * when the user navigates from a marketing route into an app/auth/admin
 * route within the same SPA session. main.tsx already skips these
 * bundles on initial load for marketing pathnames; this fills the
 * mid-session navigation case so the destination route isn't unstyled.
 * Idempotent — the module-level guard means we only fetch once per tab.
 * Owen 2026-05-29 (Ship 11 / Lever 1).
 */
let appCssLoaded = false;
function AppCssLoader() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (appCssLoaded) return;
    const isApp = !(
      pathname === "/" ||
      pathname.startsWith("/articles") ||
      pathname === "/get-started" ||
      pathname === "/overview" ||
      pathname === "/privacy"
    );
    if (isApp) {
      appCssLoaded = true;
      Promise.all([
        import("./styles/foreman-ui-app.css"),
        import("./styles/foreman-ui-automations.css"),
      ]);
    }
  }, [pathname]);
  return null;
}

const UI_ONLY_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";
// Temporary debug overlay — when on, the DevPanel + MemoryPanel render on the
// right of chat regardless of NEW_UI muting. Drives DevPanel.open=true so it
// isn't hidden behind the as-link-chat layout shift.
const SHOW_DEVBAR =
  String(import.meta.env.VITE_SHOW_DEVBAR ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_SHOW_DEVBAR ?? "").toLowerCase() === "true";
const NEW_UI =
  String(import.meta.env.VITE_NEW_UI ?? "true").toLowerCase() === "1" ||
  String(import.meta.env.VITE_NEW_UI ?? "true").toLowerCase() === "true";

// Hub previews append ?preview=1 to bypass UI_ONLY_MODE auth redirects
// so screens like /login render their actual content in the iframe.
function isPreviewQuery(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("preview") === "1";
}
import { listWorkflows, clearChat, listAutomations } from "@/api/client";
import type { WorkflowSummary, AutomationSummary } from "@/types";
import { useChatManager, type ChatSession } from "@/hooks/useChatManager";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationToasts } from "@/components/Chat/NotificationToast";
import { MemoryEventFeed } from "@/components/Chat/MemoryEventFeed";
import Sidebar from "@/components/Layout/Sidebar";
import AppShellNew from "@/components/Layout/AppShellNew";
// ── Marketing routes ──────────────────────────────────────────────
// LandingPageNew stays sync (NEW_UI=true homepage, LCP critical path).
// LandingPage (old) + OverviewPage go lazy — they pull in framer-motion
// via Landing/FadeIn, which used to land on the marketing critical
// path even when LandingPageNew rendered. Owen 2026-05-29 (Ship 11 /
// Lever 3): framer-motion now only loads on /overview or the NEW_UI=false
// fallback render.
// ArticlesPage{,New} + ArticlePage{,New} also lazy (Lever 3 from Ship 3):
// they import articles.ts (~160 KB raw / ~40 KB gz), hoisted into a
// shared chunk loaded only when /articles or /articles/<slug> is hit.
import LandingPageNew from "@/pages/LandingPageNew";
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const OverviewPage = lazy(() => import("@/pages/OverviewPage"));
const ArticlesPage = lazy(() => import("@/pages/ArticlesPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const ArticlesPageNew = lazy(() => import("@/pages/ArticlesPageNew"));
const ArticlePageNew = lazy(() => import("@/pages/ArticlePageNew"));
import PrivacyPage from "@/pages/PrivacyPage";
import GetStartedPage from "@/pages/GetStartedPage";
import NotFoundPage from "@/pages/NotFoundPage";
// ── App / admin surface (LAZY — pulled out of the marketing bundle).
// Each React.lazy creates its own async chunk; Vite tree-shakes shared
// deps into a vendor chunk. The whole AppLayout body render is wrapped
// in a Suspense at the bottom of TopLevelRoutes / AppLayout. ──────
const ChatView = lazy(() => import("@/components/Chat/ChatView"));
const WorkflowsView = lazy(() => import("@/components/Workflows/WorkflowsView"));
const AutomationsView = lazy(() => import("@/components/Automations/AutomationsView"));
const AutomationsViewNew = lazy(() => import("@/components/Automations/AutomationsViewNew"));
const ActionsView = lazy(() => import("@/components/Actions/ActionsView"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const LoginPageNew = lazy(() => import("@/pages/LoginPageNew"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const OrgPage = lazy(() => import("@/pages/OrgPage"));
const EmailPage = lazy(() => import("@/pages/EmailPage"));
const WhatsAppPage = lazy(() => import("@/pages/WhatsAppPage"));
const GatewayPage = lazy(() => import("@/pages/GatewayPage"));
const CrmPage = lazy(() => import("@/pages/CrmPage"));
const MemoryPage = lazy(() => import("@/pages/MemoryPage"));
const OrgIntelPage = lazy(() => import("@/pages/OrgIntelPage"));
const KnowledgeInspectorPage = lazy(
  () => import("@/pages/KnowledgeInspectorPage"),
);
const OnboardPage = lazy(() => import("@/pages/OnboardPage"));
const OnboardPageNew = lazy(() => import("@/pages/OnboardPageNew"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));
const KnowledgePage = lazy(() => import("@/pages/KnowledgePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const LookoutsPage = lazy(() => import("@/pages/LookoutsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
// Admin sub-routes share the same `admin/routes` module — one lazy
// import gives Vite the whole admin chunk. Each named export becomes
// a lazy component via Object destructuring on the resolved module.
const adminRoutesPromise = () => import("@/components/admin/routes");
const AdminOverviewRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminOverviewRoute })));
const AdminUsersRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminUsersRoute })));
const AdminTenantsRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminTenantsRoute })));
const AdminKnowledgeRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminKnowledgeRoute })));
const AdminErrorsRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminErrorsRoute })));
const AdminAnalyticsRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminAnalyticsRoute })));
const AdminInfraRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminInfraRoute })));
const AdminCostsRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminCostsRoute })));
const AdminAuditRoute = lazy(() => adminRoutesPromise().then((m) => ({ default: m.AdminAuditRoute })));
import ProtectedRoute from "@/auth/ProtectedRoute";
import { useAuth } from "@/auth/AuthContext";
import ReportIssueModal from "@/components/ReportIssueModal";
import { EmailProvider, useEmailContext } from "@/contexts/EmailContext";
import { encodeEmailDraft } from "@/components/Chat/EmailDraftCard";

/** Suspense fallback for lazy marketing routes. Owen 2026-05-28:
 *  was a 32px faded "Foreman" wordmark for Lighthouse FCP detection,
 *  but the grey wordmark on reload read as broken. Returning null
 *  trades a brief blank flash (typically <200ms once the article
 *  chunk lands) for no visible glitch. PSI FCP on /articles/* may
 *  regress a touch; LCP is unaffected since it fires after the
 *  real page renders. */
const MarketingRouteFallback = () => null;

/** Extract the short UUID portion from a session ID (strips tenant__user__ prefix) */
function chatIdToSlug(id: string): string {
  const parts = id.split("__");
  return parts[parts.length - 1];
}

/** Syncs URL :chatId param with active chat state */
function ChatRouteSync({
  activeChatId,
  onSwitchChat,
  chatsLoaded,
  sessions,
  children,
}: {
  activeChatId: string;
  onSwitchChat: (id: string) => void;
  chatsLoaded: boolean;
  sessions: ChatSession[];
  children: React.ReactNode;
}) {
  const { chatId: urlSlug } = useParams<{ chatId: string }>();
  const initialLoad = useRef(true);
  useEffect(() => {
    if (!urlSlug || !chatsLoaded) return;
    // Skip URL→state sync on initial page load so fresh blank chat prevails
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    // Find session whose ID ends with the URL slug
    const match = sessions.find((s) => chatIdToSlug(s.id) === urlSlug);
    if (match && match.id !== activeChatId) {
      onSwitchChat(match.id);
    }
  }, [urlSlug, chatsLoaded, sessions, activeChatId, onSwitchChat]);

  return <>{children}</>;
}

/**
 * Shared shape passed down through <Outlet context={...}/>. Route components
 * grab it with `useOutletContext<AppOutletContext>()`. Keeps AppLayout as
 * pure plumbing and each route as a self-contained leaf.
 */
type AppOutletContext = {
  chat: ReturnType<typeof useChatManager>;
  handleSend: (text: string, fileRef?: string) => void;
  handleAutomationCountChange: (reports: number, workflows: number) => void;
  devOpen: boolean;
  memoryPanelOpen: boolean;
  memoryEnabled: boolean;
  toggleMemory: () => void;
  /** Stop button — request the in-flight planner turn to abort.
   * Aliased from chat.cancel so route components don't have to know
   * about the hook surface. */
  onCancel: () => void;
};

function useAppCtx() {
  return useOutletContext<AppOutletContext>();
}

function ChatRouteIndex() {
  const { chat, handleSend, devOpen, memoryPanelOpen, memoryEnabled, toggleMemory, onCancel } = useAppCtx();
  return (
    <ChatView
      key={chat.activeChatId || "loading"}
      messages={chat.activeSession?.messages ?? []}
      pipelineRuns={chat.activeSession?.pipelineRuns ?? []}
      sessionCost={chat.activeSession?.sessionCost ?? 0}
      sending={chat.sending}
      statusText={chat.statusText}
      onSend={handleSend}
      onClear={chat.clearCurrentChat}
      devOpen={SHOW_DEVBAR ? true : NEW_UI ? false : devOpen}
      memoryPanelOpen={SHOW_DEVBAR ? true : NEW_UI ? false : memoryPanelOpen}
      pendingConfirm={chat.pendingConfirm}
      onApproveWrite={chat.approveWrite}
      onRejectWrite={chat.rejectWrite}
      onApproveChain={chat.approveChain}
      memoryEnabled={memoryEnabled}
      onToggleMemory={toggleMemory}
      memoryRecalls={chat.activeSession?.memoryRecalls ?? []}
      httpTrace={chat.activeSession?.httpTrace ?? []}
      streamingAssistantText={chat.activeSession?.streamingAssistantText ?? ""}
      intentTrail={chat.activeSession?.intentTrail ?? []}
      onCancel={onCancel}
      goal={chat.activeSession?.goal}
      topicUnderstanding={chat.activeSession?.topicUnderstanding ?? null}
      topicPathChainDepth={chat.activeSession?.topicPathChainDepth ?? null}
      proactivelyLoaded={chat.activeSession?.proactivelyLoaded ?? []}
      costRollup={chat.activeSession?.costRollup ?? null}
    />
  );
}

function ChatRouteWithId() {
  const { chat, handleSend, devOpen, memoryPanelOpen, memoryEnabled, toggleMemory, onCancel } = useAppCtx();
  return (
    <ChatRouteSync
      activeChatId={chat.activeChatId}
      onSwitchChat={chat.switchChat}
      chatsLoaded={chat.chatsLoaded}
      sessions={chat.sessions}
    >
      {chat.activeSession ? (
        <ChatView
          key={chat.activeChatId}
          messages={chat.activeSession.messages}
          pipelineRuns={chat.activeSession.pipelineRuns}
          sessionCost={chat.activeSession.sessionCost}
          sending={chat.sending}
          statusText={chat.statusText}
          onSend={handleSend}
          onClear={chat.clearCurrentChat}
          devOpen={SHOW_DEVBAR ? true : NEW_UI ? false : devOpen}
          memoryPanelOpen={SHOW_DEVBAR ? true : NEW_UI ? false : memoryPanelOpen}
          pendingConfirm={chat.pendingConfirm}
          onApproveWrite={chat.approveWrite}
          onRejectWrite={chat.rejectWrite}
          onApproveChain={chat.approveChain}
          memoryEnabled={memoryEnabled}
          onToggleMemory={toggleMemory}
          memoryRecalls={chat.activeSession.memoryRecalls}
          httpTrace={chat.activeSession.httpTrace}
          streamingAssistantText={chat.activeSession.streamingAssistantText}
          intentTrail={chat.activeSession.intentTrail}
          onCancel={onCancel}
          goal={chat.activeSession.goal}
          topicUnderstanding={chat.activeSession.topicUnderstanding}
          topicPathChainDepth={chat.activeSession.topicPathChainDepth}
          proactivelyLoaded={chat.activeSession.proactivelyLoaded}
          costRollup={chat.activeSession.costRollup}
        />
      ) : null}
    </ChatRouteSync>
  );
}

function ActionsRoute() {
  const { handleAutomationCountChange } = useAppCtx();
  // NEW_UI surface reads from the new /api/actions endpoint.
  // Legacy NEW_UI=false branch keeps AutomationsView pointing at
  // /api/automations so the old rows stay accessible until they're
  // migrated or sunset.
  return NEW_UI ? (
    <ActionsView onCountChange={(n) => handleAutomationCountChange(0, n)} />
  ) : (
    <AutomationsView key="actions" mode="workflow" onCountChange={handleAutomationCountChange} />
  );
}

function ReportsRoute() {
  const { handleAutomationCountChange } = useAppCtx();
  return NEW_UI ? (
    <AutomationsViewNew key="reports" mode="report" onCountChange={handleAutomationCountChange} />
  ) : (
    <AutomationsView key="reports" mode="report" onCountChange={handleAutomationCountChange} />
  );
}

/**
 * @deprecated kept temporarily for reference; replaced by nested children
 * routes + Outlet pattern. Will be removed once nav is verified working.
 */
function AppBody(props: {
  chat: ReturnType<typeof useChatManager>;
  handleSend: (text: string, fileRef?: string) => void;
  handleAutomationCountChange: (reports: number, workflows: number) => void;
  devOpen: boolean;
  memoryPanelOpen: boolean;
  memoryEnabled: boolean;
  toggleMemory: () => void;
}) {
  const { chat, handleSend, handleAutomationCountChange, devOpen, memoryPanelOpen, memoryEnabled, toggleMemory } = props;
  const location = useLocation();
  const path = location.pathname;

  const chatViewProps = {
    messages: chat.activeSession?.messages ?? [],
    pipelineRuns: chat.activeSession?.pipelineRuns ?? [],
    sessionCost: chat.activeSession?.sessionCost ?? 0,
    sending: chat.sending,
    statusText: chat.statusText,
    onSend: handleSend,
    onClear: chat.clearCurrentChat,
    devOpen: SHOW_DEVBAR ? true : NEW_UI ? false : devOpen,
    memoryPanelOpen: SHOW_DEVBAR ? true : NEW_UI ? false : memoryPanelOpen,
    pendingConfirm: chat.pendingConfirm,
    onApproveWrite: chat.approveWrite,
    onRejectWrite: chat.rejectWrite,
    onApproveChain: chat.approveChain,
    memoryEnabled,
    onToggleMemory: toggleMemory,
    memoryRecalls: chat.activeSession?.memoryRecalls ?? [],
    httpTrace: chat.activeSession?.httpTrace ?? [],
    streamingAssistantText: chat.activeSession?.streamingAssistantText ?? "",
    intentTrail: chat.activeSession?.intentTrail ?? [],
    onCancel: chat.cancel,
    goal: chat.activeSession?.goal,
    // v2 Part 6 (Owen + bug-diagnoser 2026-05-30 fix) — these four were
    // missing from chatViewProps. The /app/chat/<id> route at body line
    // ~392 spreads this props bag into <ChatView/>, so leaving them out
    // means DevPanel receives undefined → ConversationUnderstander
    // shows all dashes / SESSION COST $0.0000 / PROACTIVELY LOADED empty
    // even though the SSE events arrive and the session state is correct.
    topicUnderstanding: chat.activeSession?.topicUnderstanding ?? null,
    topicPathChainDepth: chat.activeSession?.topicPathChainDepth ?? null,
    proactivelyLoaded: chat.activeSession?.proactivelyLoaded ?? [],
    costRollup: chat.activeSession?.costRollup ?? null,
  };

  let body: ReactNode = null;
  if (path === "/app" || path === "/app/") {
    body = <Navigate to="/app/chat" replace />;
  } else if (path.startsWith("/app/chat/")) {
    body = (
      <ChatRouteSync
        activeChatId={chat.activeChatId}
        onSwitchChat={chat.switchChat}
        chatsLoaded={chat.chatsLoaded}
        sessions={chat.sessions}
      >
        {chat.activeSession ? (
          <ChatView key={chat.activeChatId} {...chatViewProps} messages={chat.activeSession.messages} pipelineRuns={chat.activeSession.pipelineRuns} sessionCost={chat.activeSession.sessionCost} memoryRecalls={chat.activeSession.memoryRecalls} httpTrace={chat.activeSession.httpTrace} />
        ) : null}
      </ChatRouteSync>
    );
  } else if (path === "/app/chat") {
    body = <ChatView key={chat.activeChatId || "loading"} {...chatViewProps} />;
  } else if (path === "/app/actions" || path.startsWith("/app/actions/")) {
    body = NEW_UI ? (
      <ActionsView onCountChange={(n) => handleAutomationCountChange(0, n)} />
    ) : (
      <AutomationsView key="actions" mode="workflow" onCountChange={handleAutomationCountChange} />
    );
  } else if (path === "/app/workflows" || path.startsWith("/app/workflows/")) {
    body = <Navigate to="/app/actions" replace />;
  } else if (path === "/app/reports" || path.startsWith("/app/reports/")) {
    body = NEW_UI ? (
      <AutomationsViewNew key="reports" mode="report" onCountChange={handleAutomationCountChange} />
    ) : (
      <AutomationsView key="reports" mode="report" onCountChange={handleAutomationCountChange} />
    );
  } else if (path === "/app/profile") {
    body = NEW_UI ? <Navigate to="/app/settings#general" replace /> : <ProfilePage />;
  } else if (path === "/app/organisation") {
    body = NEW_UI ? <Navigate to="/app/settings#team" replace /> : <OrgPage />;
  } else if (path === "/app/org-intel") {
    body = <Navigate to="/app/memory" replace />;
  } else if (path === "/app/email") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <EmailPage />;
  } else if (path === "/app/whatsapp") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <WhatsAppPage />;
  } else if (path === "/app/crm") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <CrmPage />;
  } else if (path === "/app/gateway") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <GatewayPage />;
  } else if (path === "/app/memory") {
    body = NEW_UI ? <Navigate to="/app/settings#privacy" replace /> : <MemoryPage />;
  } else if (NEW_UI && path === "/app/memory-legacy") {
    body = <MemoryPage />;
  } else if (NEW_UI && path === "/app/audit") {
    body = <AuditPage />;
  } else if (NEW_UI && path === "/app/knowledge") {
    body = <KnowledgePage />;
  } else if (NEW_UI && path === "/app/settings") {
    body = <SettingsPage />;
  } else if (NEW_UI && path === "/app/lookouts") {
    body = <LookoutsPage />;
  } else if (NEW_UI && path === "/app/watchdogs") {
    body = <Navigate to="/app/lookouts" replace />;
  }

  return (
    <div key={path} style={{ display: "contents" }}>
      <Suspense
        fallback={
          /* Non-null fallback so Lighthouse registers FCP even if a
             lazy chunk is still loading. The hero text gives a real
             content paint event without locking layout (CLS=0 since
             we reserve the same vertical space the route would
             occupy). Hidden once the route resolves. */
          <div
            className="cwv-route-fallback"
            style={{
              minHeight: "60vh",
              padding: "48px 24px",
              fontFamily:
                "var(--font-display, 'Clash Grotesk', system-ui, sans-serif)",
              color: "var(--ink, #141c10)",
              fontSize: "32px",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              opacity: 0.35,
            }}
            aria-hidden
          >
            Foreman
          </div>
        }
      >
        {body}
      </Suspense>
    </div>
  );
}

function AppLayout() {
  const { user } = useAuth();
  const [workflowsList, setWorkflowsList] = useState<WorkflowSummary[]>([]);
  const [reportCount, setReportCount] = useState(0);
  const [workflowCount, setWorkflowCount] = useState(0);
  const [devOpen, setDevOpen] = useState(false);
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const memoryEnabledRef = useRef(memoryEnabled);
  memoryEnabledRef.current = memoryEnabled;

  const handleAutomationCountChange = useCallback(
    (reports: number, workflows: number) => {
      setReportCount(reports);
      setWorkflowCount(workflows);
    },
    [],
  );

  const refreshWorkflows = useCallback((preloaded?: WorkflowSummary[]) => {
    if (preloaded) {
      setWorkflowsList(preloaded);
      return;
    }
    listAutomations()
      .then((items) => {
        setReportCount(items.filter((a) => a.type === "report").length);
        setWorkflowCount(items.filter((a) => a.type === "workflow").length);
        // Map to WorkflowSummary for backward compat
        setWorkflowsList(
          items.map((a) => ({
            id: a.id,
            name: a.name,
            description: a.description,
            steps_count: 0,
            input_columns: a.input_columns || [],
            output_columns: a.output_columns || {},
          })),
        );
      })
      .catch(() => {});
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const { setEmailMeta, setPendingDraft, pendingDraft } = useEmailContext();
  const chat = useChatManager(
    refreshWorkflows,
    memoryEnabledRef,
    user?.userId,
    user?.tenantId ?? undefined,
    (meta) => {
      setEmailMeta({
        ...meta,
        userId: user?.userId ?? "default_user",
        tenantId: user?.tenantId ?? "default",
      });
    },
  );

  // Email context — stores metadata for Send button in drafts
  const pendingDraftRef = useRef(pendingDraft);
  pendingDraftRef.current = pendingDraft;

  // Proactive gateway notifications — inject into chat as assistant messages
  const {
    unread,
    dismiss: dismissNotification,
    memoryEvents,
    activeMemoryEvents,
    dismissMemoryEvent,
    dismissAllMemoryEvents,
  } = useNotifications({
    tenantId: user?.tenantId ?? "default",
    userId: user?.userId ?? "default_user",
    onNotification: (n) => {
      if (n.emailData) {
        const { from, senderName, draftBody, messageId, subject } = n.emailData;
        const displayName = senderName || from.split("@")[0];

        // Split out draft sections (To: + Subject: between ---) and defer them
        const parts = draftBody.split(/^---$/m);
        const textParts: string[] = [];
        let draftContent = "";
        for (const part of parts) {
          const t = part.trim();
          if (!t) continue;
          if (
            /\*{0,2}To:\*{0,2}/i.test(t) &&
            /\*{0,2}Subject:\*{0,2}/i.test(t)
          ) {
            draftContent = t;
          } else {
            textParts.push(t);
          }
        }

        const msg =
          `You just received an email from **${displayName}** (${from}):\n\n` +
          textParts.join("\n\n");
        chat.injectMessage("assistant", msg);

        // Store draft for deferred display when user picks an option
        if (draftContent) {
          setPendingDraft(draftContent);
        }

        setEmailMeta({
          messageId,
          to: from,
          subject,
          userId: user?.userId ?? "default_user",
          tenantId: user?.tenantId ?? "default",
        });
        navigate("/app/chat");
      } else {
        chat.injectMessage("assistant", `**${n.jobName}**\n\n${n.text}`);
      }
    },
  });

  // On mount: load workflows (backend state is managed by useChatManager)
  useEffect(() => {
    refreshWorkflows();
  }, [refreshWorkflows]);

  // Wrap send: when user replies while a pending email draft exists,
  // show the stored draft in the green box instead of routing to the agent.
  const handleSend = useCallback(
    (text: string, fileRef?: string) => {
      if (pendingDraftRef.current) {
        const draft = pendingDraftRef.current;
        setPendingDraft(null);
        chat.injectMessage("user", text);
        chat.injectMessage(
          "assistant",
          encodeEmailDraft({
            from: "",
            subject: "",
            draftBody: draft,
          }),
        );
        return;
      }
      chat.send(text, fileRef);
    },
    [chat, setPendingDraft],
  );

  // DISABLED for debugging — this was the chat-URL-sync effect that yanked the
  // user back to /app/chat whenever chat state shifted. The guard added earlier
  // ("don't sync unless on a chat route") isn't enough — leaving it on still
  // forces redirect on first chat-load. Re-enable once the URL-sync logic is
  // re-thought (probably push it into the chat view, not the layout).
  // useEffect(() => { ... }, [chat.activeChatId, chat.chatsLoaded]);

  const toggleDev = useCallback(() => setDevOpen((v) => !v), []);
  const toggleMemoryPanel = useCallback(
    () => setMemoryPanelOpen((v) => !v),
    [],
  );
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const toggleMemory = useCallback(() => setMemoryEnabled((v) => !v), []);

  const overlays = (
    <>
      <NotificationToasts
        notifications={unread}
        onDismiss={dismissNotification}
        onClick={(n) => {
          if (n.emailData) {
            navigate("/app/chat");
          }
        }}
      />
      <MemoryEventFeed
        events={activeMemoryEvents}
        allEvents={memoryEvents}
        onDismiss={dismissMemoryEvent}
        onDismissAll={dismissAllMemoryEvents}
      />
      <ReportIssueModal
        open={reportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
      />
    </>
  );

  const path = usePathname();
  const chatViewProps = {
    messages: chat.activeSession?.messages ?? [],
    pipelineRuns: chat.activeSession?.pipelineRuns ?? [],
    sessionCost: chat.activeSession?.sessionCost ?? 0,
    sending: chat.sending,
    statusText: chat.statusText,
    onSend: handleSend,
    onClear: chat.clearCurrentChat,
    devOpen: SHOW_DEVBAR ? true : NEW_UI ? false : devOpen,
    memoryPanelOpen: SHOW_DEVBAR ? true : NEW_UI ? false : memoryPanelOpen,
    pendingConfirm: chat.pendingConfirm,
    onApproveWrite: chat.approveWrite,
    onRejectWrite: chat.rejectWrite,
    onApproveChain: chat.approveChain,
    memoryEnabled,
    onToggleMemory: toggleMemory,
    memoryRecalls: chat.activeSession?.memoryRecalls ?? [],
    httpTrace: chat.activeSession?.httpTrace ?? [],
    streamingAssistantText: chat.activeSession?.streamingAssistantText ?? "",
    intentTrail: chat.activeSession?.intentTrail ?? [],
    onCancel: chat.cancel,
    goal: chat.activeSession?.goal,
    // v2 Part 6 (Owen + bug-diagnoser 2026-05-30 fix) — these four were
    // missing from chatViewProps. The /app/chat/<id> route at body line
    // ~392 spreads this props bag into <ChatView/>, so leaving them out
    // means DevPanel receives undefined → ConversationUnderstander
    // shows all dashes / SESSION COST $0.0000 / PROACTIVELY LOADED empty
    // even though the SSE events arrive and the session state is correct.
    topicUnderstanding: chat.activeSession?.topicUnderstanding ?? null,
    topicPathChainDepth: chat.activeSession?.topicPathChainDepth ?? null,
    proactivelyLoaded: chat.activeSession?.proactivelyLoaded ?? [],
    costRollup: chat.activeSession?.costRollup ?? null,
  };

  let body: ReactNode = null;
  if (path === "/app" || path === "/app/") {
    body = <Navigate to="/app/chat" replace />;
  } else if (path.startsWith("/app/chat/")) {
    body = chat.activeSession ? (
      <ChatView key={chat.activeChatId} {...chatViewProps} messages={chat.activeSession.messages} pipelineRuns={chat.activeSession.pipelineRuns} sessionCost={chat.activeSession.sessionCost} memoryRecalls={chat.activeSession.memoryRecalls} httpTrace={chat.activeSession.httpTrace} />
    ) : null;
  } else if (path === "/app/chat") {
    body = <ChatView key={chat.activeChatId || "loading"} {...chatViewProps} />;
  } else if (path === "/app/actions" || path.startsWith("/app/actions/")) {
    body = NEW_UI ? (
      <ActionsView onCountChange={(n) => handleAutomationCountChange(0, n)} />
    ) : (
      <AutomationsView key="actions" mode="workflow" onCountChange={handleAutomationCountChange} />
    );
  } else if (path === "/app/workflows" || path.startsWith("/app/workflows/")) {
    body = <Navigate to="/app/actions" replace />;
  } else if (path === "/app/reports" || path.startsWith("/app/reports/")) {
    body = NEW_UI ? (
      <AutomationsViewNew key="reports" mode="report" onCountChange={handleAutomationCountChange} />
    ) : (
      <AutomationsView key="reports" mode="report" onCountChange={handleAutomationCountChange} />
    );
  } else if (path === "/app/profile") {
    body = NEW_UI ? <Navigate to="/app/settings#general" replace /> : <ProfilePage />;
  } else if (path === "/app/organisation") {
    body = NEW_UI ? <Navigate to="/app/settings#team" replace /> : <OrgPage />;
  } else if (path === "/app/org-intel") {
    body = <Navigate to="/app/memory" replace />;
  } else if (path === "/app/email") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <EmailPage />;
  } else if (path === "/app/whatsapp") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <WhatsAppPage />;
  } else if (path === "/app/crm") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <CrmPage />;
  } else if (path === "/app/gateway") {
    body = NEW_UI ? <Navigate to="/app/settings#integrations" replace /> : <GatewayPage />;
  } else if (path === "/app/memory") {
    body = NEW_UI ? <Navigate to="/app/settings#privacy" replace /> : <MemoryPage />;
  } else if (NEW_UI && path === "/app/memory-legacy") {
    body = <MemoryPage />;
  } else if (NEW_UI && path === "/app/audit") {
    body = <AuditPage />;
  } else if (NEW_UI && path === "/app/knowledge") {
    body = <KnowledgePage />;
  } else if (NEW_UI && path === "/app/settings") {
    body = <SettingsPage />;
  } else if (NEW_UI && path === "/app/lookouts") {
    body = <LookoutsPage />;
  } else if (NEW_UI && path === "/app/watchdogs") {
    body = <Navigate to="/app/lookouts" replace />;
  }

  const routes = (
    <div key={path} style={{ display: "contents" }}>
      <Suspense
        fallback={
          /* Non-null fallback so Lighthouse registers FCP even if a
             lazy chunk is still loading. The hero text gives a real
             content paint event without locking layout (CLS=0 since
             we reserve the same vertical space the route would
             occupy). Hidden once the route resolves. */
          <div
            className="cwv-route-fallback"
            style={{
              minHeight: "60vh",
              padding: "48px 24px",
              fontFamily:
                "var(--font-display, 'Clash Grotesk', system-ui, sans-serif)",
              color: "var(--ink, #141c10)",
              fontSize: "32px",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              opacity: 0.35,
            }}
            aria-hidden
          >
            Foreman
          </div>
        }
      >
        {body}
      </Suspense>
    </div>
  );

  if (NEW_UI) {
    return (
      <>
        {overlays}
        <AppShellNew
          workflowCount={workflowCount}
          reportCount={reportCount}
          onNewChat={chat.newChat}
          sessions={chat.sessions
            // Hide truly-stale blank sessions (default "New Chat" with
            // no messages) UNLESS it's the currently active chat —
            // Owen 2026-05-22 PM: clicking the New Chat button should
            // push a "New Chat" item into the sidebar + select it.
            // Without the active-chat exemption the new chat opens
            // invisibly: the chat surface clears but the sidebar
            // doesn't show the new entry until the user sends a
            // message (because messages.length stays 0).
            .filter((s) =>
              !(s.title === "New Chat" && s.messages.length === 0)
              || s.id === chat.activeChatId,
            )
            .map((s) => ({
              id: s.id,
              title: s.title,
              createdAt: s.createdAt,
              updatedAt: s.updatedAt,
              isActive: s.id === chat.activeChatId,
            }))
            // Most-recently-active first (matches what the relative-
            // time label shows). Falls back to createdAt when absent.
            .sort((a, b) =>
              (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt),
            )}
          chatsLoaded={chat.chatsLoaded}
          activeChatId={chat.activeChatId}
          onSwitchChat={chat.switchChat}
          onDeleteChat={chat.deleteChat}
          onRenameChat={chat.renameChat}
        >
          {routes}
        </AppShellNew>
      </>
    );
  }

  return (
    <div className="app-layout">
      {overlays}
      <Sidebar
        workflowCount={workflowCount}
        reportCount={reportCount}
        devOpen={devOpen}
        onToggleDev={toggleDev}
        memoryPanelOpen={memoryPanelOpen}
        onToggleMemoryPanel={toggleMemoryPanel}
        sessions={chat.sessions}
        activeChatId={chat.activeChatId}
        onNewChat={chat.newChat}
        onSwitchChat={chat.switchChat}
        onDeleteChat={chat.deleteChat}
        onEndSession={chat.endSessionChat}
        collapsed={!sidebarOpen}
        onToggleCollapse={toggleSidebar}
        chatsLoaded={chat.chatsLoaded}
        onReportIssue={() => setReportIssueOpen(true)}
      />
      {routes}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppCssLoader />
      <Routes>
        <Route
          path="/"
          element={
            NEW_UI ? (
              <LandingPageNew />
            ) : (
              <Suspense fallback={<MarketingRouteFallback />}>
                <LandingPage />
              </Suspense>
            )
          }
        />
        <Route
          path="/overview"
          element={
            <Suspense fallback={<MarketingRouteFallback />}>
              <OverviewPage />
            </Suspense>
          }
        />
        <Route
          path="/articles"
          element={
            <Suspense fallback={<MarketingRouteFallback />}>
              {NEW_UI ? <ArticlesPageNew /> : <ArticlesPage />}
            </Suspense>
          }
        />
        <Route
          path="/articles/:slug"
          element={
            <Suspense fallback={<MarketingRouteFallback />}>
              {NEW_UI ? <ArticlePageNew /> : <ArticlePage />}
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            UI_ONLY_MODE && !isPreviewQuery() ? (
              <Navigate to="/app" replace />
            ) : NEW_UI ? (
              <LoginPageNew />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path="/admin" element={<AdminPage />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview"  element={<AdminOverviewRoute />} />
          <Route path="users"     element={<AdminUsersRoute />} />
          <Route path="tenants"   element={<AdminTenantsRoute />} />
          <Route path="knowledge" element={<AdminKnowledgeRoute />} />
          <Route path="errors"    element={<AdminErrorsRoute />} />
          <Route path="analytics" element={<AdminAnalyticsRoute />} />
          <Route path="infra"     element={<AdminInfraRoute />} />
          <Route path="costs"     element={<AdminCostsRoute />} />
          <Route path="audit"     element={<AdminAuditRoute />} />
        </Route>
        {SHOW_DEVBAR && (
          <Route
            path="/dev/knowledge"
            element={<KnowledgeInspectorPage />}
          />
        )}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route
          path="/get-started"
          element={
            UI_ONLY_MODE ? <Navigate to="/app" replace /> : <GetStartedPage />
          }
        />
        <Route
          path="/onboard"
          element={
            NEW_UI ? (
              <OnboardPageNew />
            ) : UI_ONLY_MODE ? (
              <Navigate to="/app" replace />
            ) : (
              <ProtectedRoute>
                <OnboardPage />
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <EmailProvider>
                <AppLayout />
              </EmailProvider>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
